<template>
  <LModal
    :visible="visible"
    :title="t('modules.sqdc.components.workflow.SQDCWorkflowEditor.transitionRequirements')"
    ok-variant="success"
    :ok-disabled="
      (requiresComment && comment.trim() === '') ||
      (requiresDatapoint && selectedDatapointId == null)
    "
    :submit="submit"
    @update:visible="emit('update:visible', $event)"
  >
    <InputRow
      v-if="requiresComment"
      v-model="comment"
      name="workflowTransitionComment"
      type="textarea"
      required
      :display-name="t('components.TaskModal.comment')"
      :auto-resize="true"
      rows="3"
    />
    <InputRow
      v-if="requiresDatapoint"
      :model-value="selectedDatapointId"
      name="workflowTransitionDatapoint"
      required
      :display-name="t('modules.sqdc.components.workflow.SQDCWorkflowEditor.selectDatapoint')"
    >
      <template #default="{ field }">
        <LISAInputSelectList
          v-bind="field"
          v-model="selectedDatapointId"
          :options="datapointOptions"
          class="w-100"
        />
      </template>
    </InputRow>
  </LModal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useTranslation } from "@/language/translation";
import LModal from "@/components/LModal.vue";
import InputRow from "@/components/shared/form/InputRow.vue";
import LISAInputSelectList from "@/components/primevueWrapper/LISAInputSelectList.vue";

export type SQDCWorkflowDatapointCandidate = {
  id: number;
  timestamp: string;
  quantity: number;
  typeName: string;
  costCenterName?: string;
};

const props = defineProps<{
  visible: boolean;
  requiresComment: boolean;
  datapoints: SQDCWorkflowDatapointCandidate[];
}>();
const emit = defineEmits<{
  "update:visible": [visible: boolean];
  proceed: [value: { transitionComment?: string; selectedDatapointId?: number }];
}>();
const { d, t } = useTranslation();
const comment = ref("");
const selectedDatapointId = ref<number>();
const requiresDatapoint = computed(() => props.datapoints.length > 1);
const showCostCenter = computed(
  () => new Set(props.datapoints.map((datapoint) => datapoint.costCenterName ?? "")).size > 1,
);
const datapointOptions = computed(() =>
  props.datapoints.map((datapoint) => ({
    value: datapoint.id,
    label: [
      datapoint.typeName,
      d(new Date(datapoint.timestamp), "long"),
      String(datapoint.quantity),
      ...(showCostCenter.value && datapoint.costCenterName ? [datapoint.costCenterName] : []),
    ].join(" · "),
  })),
);

watch(
  () => props.visible,
  (visible) => {
    if (!visible) return;
    comment.value = "";
    selectedDatapointId.value = undefined;
  },
);

async function submit() {
  emit("proceed", {
    transitionComment: comment.value.trim() || undefined,
    selectedDatapointId: selectedDatapointId.value,
  });
  return { success: true };
}
</script>
